import React, { useState, useRef, useEffect } from 'react'
import { Box, Input, Flex, Text, BoxProps } from '@chakra-ui/react'
import { useController, Control } from 'react-hook-form'
import Icon from '../shared/icons'
import { InputGroup } from '@/components/ui/input-group'
import nameAttributeConvert from '@/utils/name-attributes-convert'
import { NameAttributes } from '@/entities/api/products/name-attributes'

const useClickOutside = (
	ref: React.RefObject<HTMLElement | null>,
	handler: () => void
) => {
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				handler()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [ref, handler])
}

const Divider = () => (
	<Box borderBottomWidth='1px' borderColor='gray.200' my={0} />
)

const List = ({ children }: { children: React.ReactNode }) => (
	<Box as='ul' listStyleType='none' m={0} p={0}>
		{children}
	</Box>
)

interface ListItemCustomProps extends BoxProps {
	children: React.ReactNode
}

const ListItemCustom = ({ children, ...props }: ListItemCustomProps) => (
	<Box as='li' {...props}>
		{children}
	</Box>
)

export type SelectOption = {
	label: string
	value: string | number | boolean
	type?: string
}

type SelectProps = {
	name?: string
	label?: string
	placeholder?: string
	options?: SelectOption[]
	control: Control<any>
	selectValue?: (value: SelectOption) => void
	isRequired?: boolean
	isDisabled?: boolean
	onChange?: (value: string | boolean | number) => void
	defaultValue?: string | SelectOption
	reset?: boolean
}

const Select: React.FC<SelectProps> = ({
	name = 'default',
	label = 'Selecciona una opcion',
	placeholder = 'Debes selecionar alguna opcion',
	options = [],
	control,
	isRequired = false,
	isDisabled = false,
	selectValue,
	onChange,
	defaultValue = '',
	reset = false,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [hasBeenInitialized, setHasBeenInitialized] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	const processedDefaultValue = React.useMemo(() => {
		if (typeof defaultValue === 'object' && defaultValue !== null) {
			return defaultValue.value
		}
		return defaultValue
	}, [defaultValue])

	const { field, fieldState } = useController({
		name,
		control,
		defaultValue: processedDefaultValue,
	})

	// Efecto para inicializar el valor por defecto solo una vez
	useEffect(() => {
		if (
			!hasBeenInitialized &&
			typeof defaultValue === 'object' &&
			defaultValue !== null &&
			'value' in defaultValue &&
			'label' in defaultValue
		) {
			field.onChange(defaultValue.value)
			setHasBeenInitialized(true)

			if (selectValue) {
				selectValue(defaultValue as SelectOption)
			}

			if (onChange) {
				onChange(defaultValue.value)
			}
		} else if (!hasBeenInitialized && defaultValue !== '') {
			// Para valores por defecto que son strings/numbers/booleans
			field.onChange(defaultValue)
			setHasBeenInitialized(true)

			if (onChange) {
				onChange(defaultValue as string | number | boolean)
			}
		} else if (!hasBeenInitialized) {
			// Marcar como inicializado incluso si no hay defaultValue
			setHasBeenInitialized(true)
		}
	}, [defaultValue, hasBeenInitialized, field, selectValue, onChange])

	useEffect(() => {
		if (reset) {
			field.onChange('')
			setSearchTerm('')
			setIsOpen(false)
			setHasBeenInitialized(false) // Permitir reinicialización después del reset
		}
	}, [reset, field])

	const selectedOption = options.find((option) => option.value === field.value)

	const toggleDropdown = () => {
		if (!isDisabled) {
			setIsOpen(!isOpen)
			if (!isOpen) {
				setSearchTerm('')
				setTimeout(() => {
					if (inputRef.current) {
						inputRef.current.focus()
					}
				}, 10)
			}
		}
	}

	const handleSelect = (option: SelectOption) => {
		selectValue?.(option)
		field.onChange(option.value)
		if (onChange) {
			onChange(option.value)
		}
		setIsOpen(false)
	}

	useClickOutside(dropdownRef, () => setIsOpen(false))

	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	)

	return (
		<Box position='relative' width='full' mt={4} mb={4} ref={dropdownRef}>
			<Text mb={2} color='black'>
				{label}{' '}
				{isRequired && (
					<Text as='span' color='red.500'>
						*
					</Text>
				)}
			</Text>

			<Flex
				onClick={toggleDropdown}
				cursor={isDisabled ? 'not-allowed' : 'pointer'}
				p={3}
				borderWidth={1}
				borderColor={fieldState.error ? 'red.500' : 'gray.200'}
				borderRadius='md'
				bg={isDisabled ? 'gray.100' : 'white'}
				color={isDisabled ? 'gray.500' : 'black'}
				justifyContent='space-between'
				alignItems='center'
				opacity={isDisabled ? 0.6 : 1}
				_hover={!isDisabled ? { borderColor: 'gray.300' } : {}}
				_focus={
					!isDisabled
						? { borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }
						: {}
				}
			>
				<Text overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
					{selectedOption ? selectedOption.label : placeholder}
				</Text>
				<Icon icon='chevronDown' size='lg' color='black' />
			</Flex>

			{fieldState.error && (
				<Text color='red.500' fontSize='sm' mt={1}>
					{fieldState.error.message}
				</Text>
			)}

			{isOpen && (
				<Box
					position='absolute'
					width='full'
					mt={2}
					bg='white'
					borderWidth={1}
					borderColor='gray.200'
					borderRadius='md'
					boxShadow='lg'
					zIndex={10}
					maxHeight='300px'
					display='flex'
					flexDirection='column'
				>
					<Box p={3} borderBottomWidth={1} borderColor='gray.200'>
						<InputGroup
							w='full'
							startElement={
								<Box ml='2'>
									<Icon icon='search' size='lg' color='gray.400' />
								</Box>
							}
						>
							<Input
								ref={inputRef}
								placeholder='Buscar opciones...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onClick={(e) => e.stopPropagation()}
								_focus={{ borderColor: 'blue.500' }}
							/>
						</InputGroup>
					</Box>

					<Divider />

					<Box overflowY='auto' maxHeight='232px'>
						{filteredOptions.length > 0 ? (
							<List>
								{filteredOptions.map((option, index) => (
									<ListItemCustom
										key={`${option.value}-${index}`}
										onClick={() => handleSelect(option)}
										p={4}
										py={2}
										_hover={{ bg: 'gray.100' }}
										cursor='pointer'
										bg={field.value === option.value ? 'gray.50' : ''}
										fontWeight={
											field.value === option.value ? 'bold' : 'normal'
										}
										display='flex'
										alignItems='center'
									>
										{option.type
											? `${nameAttributeConvert(
													option?.type as NameAttributes
											  )} - ${option.label}`
											: option.label}
									</ListItemCustom>
								))}
							</List>
						) : (
							<Box p={4} textAlign='center' color='gray.500'>
								No hay opciones disponibles
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default Select